/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import error from '../../../../../common/nodejs/errors';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../../share/util/api/polling';
import session from '../../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  FAILED: 'failed',
  ERROR: 'error',
  SUCCESS: 'success'
};

export default {
  name: 'requirement50004',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      softwareInfo: [],
      // ダイアログフラグ
      modal_requirement_50004: false,
      readErrorCode: error.WSP_50004_READ_ERROR // Phase5,6で判定するエラーを変えるため
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 実行中
      const ExecutionTitle = 'individual.common_title_execution';
      // 正常終了
      const SuccessTitle = 'message.common_title_success';
      // データ取得エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: ExecutionTitle,
        success: SuccessTitle,
        failed: ErrorTitle,
        error: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        failed: `individual.error_failed_get_software_parts_number`,
        error: 'individual.error_communication_error'
      };
      return this.$t(messageMap[this.phase]);
    },

  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_50004 = true;
      this.wsIdToReadError();
    },

    /** 呼び出された機能別にデータ取得エラーのコードを判定するメソッド */
    wsIdToReadError() {
      switch (this.workSupportId) {
        case 6000401:
          this.readErrorCode = error.WSP_60004_READ_ERROR;
          break;
        default:
          this.readErrorCode = error.WSP_50004_READ_ERROR;
          break;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_50004 = false;
    },
    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** データ読み出し処理 */
    async execute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        // データ読み出し
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
          // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        // response が存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了：読み出しデータ表示
            this.softwareInfo = response.data.user_data.read_data;
            this.phase = PHASE.SUCCESS;
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了
            switch (response.data.async_code) {
              case this.readErrorCode:
                // データ取得エラー
                this.phase = PHASE.FAILED;
                break;
              default:
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            this.showUnexpectedError();
            break;
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },
  },

  async mounted() {
    this.init();
    // データ読み出し
    await this.execute();
  },
};